import React, { useEffect, useRef } from "react";
import { ArrowUpRight, Moon, Sun } from "lucide-react";
import "./ContactInfo.css";
import { useTheme } from "../hooks/useTheme";
import ActivityAccordion from "./ActivityAccordion";

const PROJECTS = [
  {
    name: "onKaul",
    tagline: "An open-source AI teammate for developers.",
    description:
      "One AI platform across Slack, Jira, the terminal, and the browser. Built to investigate production issues, understand codebases, and help engineers go from problem to pull request.",
    url: "https://onkaul.cloud/",
    badge: "Open source",
    tags: ["Python", "Slack API", "Jira", "LLMs"],
  },
  {
    name: "Kids Art to Life",
    tagline: "Turning little drawings into big adventures.",
    description:
      "An AI-powered app that brings children's drawings to life as animated stories. Built for my daughter, and for the magic of seeing her imagination come alive.",
    url: "https://kidsartto.life/",
    badge: "Live",
    tags: ["React", "FastAPI", "MiniMax", "AWS"],
  },
  {
    name: "Color Life",
    tagline: "Any photo. A coloring page in seconds.",
    description:
      "Turn your favorite photos into printable coloring pages with AI. A little spin-off from Kids Art to Life, made for kids who love to color.",
    url: "https://color.kidsartto.life/",
    badge: "Live",
    tags: ["React", "Gemini", "S3"],
  },
  {
    name: "Mom & Me Comics",
    tagline: "Your favorite memories, told as comics.",
    description:
      "Transform everyday photos of moms and kids into personalized comic stories. Made to turn small moments into something worth printing, framing, or gifting.",
    url: "https://moms.kidsartto.life/",
    badge: "Live",
    tags: ["React", "Gemini", "S3"],
  },
  {
    name: "vAI",
    tagline: "Me, but available for questions 24/7.",
    description:
      "A personal AI assistant that knows about my work, projects, and engineering philosophy. Curious about something? Ask it instead of me.",
    url: "https://vaibhavkaul.com/vAI",
    badge: "Live · on this site",
    tags: ["React", "FastAPI", "OpenAI", "EC2"],
  },
];

const STOPS = [
  {
    dates: "Jun 2023 — Present",
    company: "Taptap Send",
    url: "https://www.taptapsend.com/",
    role: "Head of Product Engineering",
    desc: "Leading engineering for cross-border remittances — money movement for immigrant communities.",
  },
  {
    dates: "Nov 2021 – May 2023",
    company: "SeatGeek",
    url: "https://www.seatgeek.com/",
    role: "Staff Software Engineer",
    desc: "Staff-level engineering on the live-event ticketing platform.",
  },
  {
    dates: "Jul 2020 – Nov 2021",
    company: "Facebook",
    url: "https://www.facebook.com/",
    role: "Engineering Manager",
    desc: "Engineering management for consumer products at global scale.",
  },
  {
    dates: "Mar 2015 – Jul 2020",
    company: "SeatGeek",
    url: "https://www.seatgeek.com/",
    role: "Director of Engineering · Senior Technical Manager · Senior Web Engineer",
    desc: "Five years across backend engineering and engineering leadership.",
  },
  {
    dates: "Sep 2012 – Mar 2015",
    company: "uSell.com",
    url: null,
    role: "Senior Engineer",
    desc: "Java/Spring backend engineering.",
  },
  {
    dates: "Aug 2010 – Sep 2012",
    company: "CommonDesk LLC",
    url: null,
    role: "Director of Engineering · Senior Engineer",
    desc: "Compliance and productivity software; mobile archive products.",
  },
  {
    dates: "Jun 2010 – Aug 2010",
    company: "Barclays Capital",
    url: null,
    role: "Technology Analyst",
    desc: "Java/Spring/Hibernate tooling.",
  },
  {
    dates: "Aug 2009 – May 2010",
    company: "NYU Courant",
    url: null,
    role: "Teaching Assistant",
    desc: "Taught undergrads Java, OO concepts, and games.",
  },
];

const LINKS = [
  { label: "LinkedIn", url: "https://www.linkedin.com/in/kaulvaibhav/" },
  { label: "Resume", url: "/files/vaibhavkaul.pdf" },
  { label: "Email", url: "mailto:me@vaibhavkaul.com" },
];

const ContactInfo = ({ isOpen, onClose }) => {
  const { theme, toggle } = useTheme();
  const scrollRef = useRef(null);
  const progressRef = useRef(null);
  const tlineRef = useRef(null);
  const fillRef = useRef(null);

  // Scroll-driven UI: top progress bar + timeline fill + stop reveal.
  // Reads geometry only; safe under jsdom (rects are zeros, no crash).
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const update = () => {
      const max = scroller.scrollHeight - scroller.clientHeight;
      const pct = max > 0 ? scroller.scrollTop / max : 0;
      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${pct})`;
      }
      const tl = tlineRef.current;
      const fill = fillRef.current;
      if (tl && fill) {
        const sRect = scroller.getBoundingClientRect();
        const tRect = tl.getBoundingClientRect();
        const scrolled = Math.min(
          Math.max(sRect.top + sRect.height * 0.65 - tRect.top, 0),
          tRect.height
        );
        fill.style.height = `${scrolled}px`;
        const vh = window.innerHeight || 800;
        tl.querySelectorAll(".stop").forEach((s) => {
          if (s.getBoundingClientRect().top < vh * 0.85) {
            s.classList.add("visible");
          }
        });
      }
    };
    scroller.addEventListener("scroll", update, { passive: true });
    update();
    return () => scroller.removeEventListener("scroll", update);
  }, []);

  const linkLabel = (url) =>
    url.replace(/^https?:\/\//, "").replace(/\/$/, "");

  return (
    <div className={`info-popup ${isOpen ? "open" : ""}`} ref={scrollRef}>
      <div className="scroll-progress" aria-hidden="true">
        <span ref={progressRef} />
      </div>
      <div className="info-content">
        <button
          className="theme-toggle"
          onClick={toggle}
          aria-label="Toggle color theme"
        >
          {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <div className="bento-hero">
          <header className="profile-hero">
            <img
              src="/images/profile.png"
              alt="Vaibhav Kaul"
              className="profile-portrait"
            />
            <div className="profile-intro">
              <h1 className="profile-name">Vaibhav Kaul</h1>
              <p className="profile-tagline">30 years of building.</p>
              <p className="profile-sub">
                Software, product and everything in between.
              </p>
              <p className="known-for">Engineering @ Taptap Send.</p>
            </div>
          </header>
          <button
            type="button"
            className="ask-vai-tile"
            onClick={onClose}
            aria-label="Ask vAI"
          >
            <span className="ask-vai-title">Ask vAI</span>
            <span className="ask-vai-sub">
              Vaibhav&rsquo;s AI alter ego — ask about his work, projects,
              opinions.
            </span>
            <span className="ask-vai-cta">Start chatting →</span>
          </button>
        </div>

        <section className="stat-row" aria-label="Highlights">
          <div className="stat">
            <span className="stat-num">30+</span>
            <span className="stat-cap">Years building things</span>
          </div>
          <div className="stat">
            <span className="stat-num">3,148</span>
            <span className="stat-cap">GitHub contributions in the last year</span>
          </div>
          <div className="stat">
            <span className="stat-num">5</span>
            <span className="stat-cap">Personal products live in 2026</span>
          </div>
        </section>

        <section className="info-section" aria-labelledby="label-personal">
          <h2 className="section-label" id="label-personal">
            01 / Personal
          </h2>
          <div className="section-body">
            <p>I live in New York City with my family.</p>
            <p>
              Outside of work it&rsquo;s football &mdash; I&rsquo;m a
              part-owner of{" "}
              <a
                href="https://stretfordpaddockfc.com/"
                target="_blank"
                rel="noreferrer"
              >
                Stretford Paddock FC
              </a>{" "}
              &mdash; and building small things with my daughter. Most of my
              projects start at the kitchen table.
            </p>
          </div>
        </section>

        <section className="info-section" aria-labelledby="label-professional">
          <h2 className="section-label" id="label-professional">
            02 / Professional
          </h2>
          <div className="section-body">
            <p>
              I lead engineering at{" "}
              <a
                href="https://www.taptapsend.com/"
                target="_blank"
                rel="noreferrer"
              >
                Taptap Send
              </a>
              . Formerly at{" "}
              <a
                href="https://www.seatgeek.com/"
                target="_blank"
                rel="noreferrer"
              >
                Seatgeek
              </a>{" "}
              and{" "}
              <a
                href="https://www.facebook.com/"
                target="_blank"
                rel="noreferrer"
              >
                Meta
              </a>
              . I believe great engineering leaders{" "}
              <mark className="bio-hl">build alongside their teams</mark>,
              write code, challenge assumptions, and get into the details. I
              value high talent density, strong individual contributors, and
              teams that own their decisions. Engineering should move things
              forward, not slow them down. And the best software starts with{" "}
              <mark className="bio-hl">understanding its users</mark>.
            </p>
          </div>

          <div className="timeline">
            <div className="timeline-head">
              <h3 className="timeline-title">Professional Experience</h3>
            </div>
            <div className="tline" ref={tlineRef}>
              <div className="tline-track" aria-hidden="true" />
              <div className="tline-fill" ref={fillRef} aria-hidden="true" />
              {STOPS.map((s) => (
                <div key={`${s.company}-${s.dates}`} className="stop">
                  <span className="stop-rail" aria-hidden="true">
                    <span className="stop-dot" />
                  </span>
                  <div className="stop-body">
                    <span className="stop-dates">{s.dates}</span>
                    <h4 className="stop-company">
                      {s.url ? (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {s.company}
                        </a>
                      ) : (
                        s.company
                      )}
                    </h4>
                    <span className="stop-role">{s.role}</span>
                    <p className="stop-desc">{s.desc}</p>
                  </div>
                </div>
              ))}
              <p className="first-stop">
                <span className="first-stop-dots" aria-hidden="true">
                  ···
                </span>{" "}
                First stop · 2007 — Sapient Global Markets, Associate Technology
                L2. RBS Bankline automation and test-automation frameworks.
              </p>
            </div>
          </div>

          <ActivityAccordion />
        </section>

        <section className="info-section" aria-labelledby="label-projects">
          <h2 className="section-label" id="label-projects">
            03 / Projects
          </h2>
          <p className="section-intro">
            Always building. Usually something that starts at the kitchen
            table.
          </p>
          <div className="project-grid">
            {PROJECTS.map((p) => (
              <a
                key={p.name}
                className="project-card"
                href={p.url}
                target="_blank"
                rel="noreferrer"
              >
                <span className="project-name-row">
                  <span className="project-name">{p.name}</span>
                  <span className="project-badge">{p.badge}</span>
                </span>
                <span className="project-tagline">{p.tagline}</span>
                <span className="project-description">{p.description}</span>
                <span className="project-tags">
                  {p.tags.map((t) => (
                    <span key={t} className="project-tag">
                      {t}
                    </span>
                  ))}
                </span>
                <span className="project-link">
                  {linkLabel(p.url)}
                  <ArrowUpRight size={15} />
                </span>
              </a>
            ))}
          </div>
        </section>

        <section className="talk-shop" aria-label="Contact">
          <h2 className="talk-shop-title">Let&rsquo;s talk shop.</h2>
          <p className="talk-shop-body">
            Engineering leadership, AI, or something you&rsquo;re building —
            always up for a good conversation.
          </p>
        </section>

        <footer className="info-footer">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={l.url}
              target={l.url.startsWith("mailto") ? undefined : "_blank"}
              rel="noreferrer"
            >
              {l.label}
            </a>
          ))}
        </footer>
      </div>
      <button
        className="vai-fab"
        onClick={onClose}
        aria-label="Chat with vAI, Vaibhav's AI alter ego"
      >
        <img src="/images/vai-avatar.jpg" alt="" aria-hidden="true" />
        <span className="vai-fab-label">Chat with vAI</span>
      </button>
    </div>
  );
};

export default ContactInfo;
